import { GeoJsonLayer } from '@deck.gl/layers';
import { map, isEmpty } from 'lodash';
import * as layerOps from '../../api/Layers/layer-operations';
import adaptURL from '../adapt-url';
import getGeoJSONFromCSV from '../get-geojson-from-csv';
import getGeoJSONFromExcel from '../get-geojson-from-excel';
import joinGeoJSONThematicLayer from '../join-geojson-thematic-layers';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import methodPromise from '../method-promise-wrapper';

const buildDeckGeoJSONLayer = async ({ layer, FindLayerVisible, activeLayers, style }) => {
  let geojsonFile = '';
  const url = adaptURL(layerOps.getURL(layer));
  try {
    switch (true) {
      case layerOps.isCSV(layer):
        geojsonFile = await getGeoJSONFromCSV(layer);
        break;
      case layerOps.isExcel(layer):
      case layerOps.isGoogleSheets(layer):
        geojsonFile = await getGeoJSONFromExcel(layer);
        break;
      case layerOps.isFiware(layer):
        geojsonFile = await methodPromise('layers.loadFiware', {
          addressIndex: layerOps.getAddressIndex(layer),
          fiwareLayerName: layerOps.getFiwareLayerName(layer),
          georefType: layerOps.getGeorefType(layer),
          loadParameters: {
            ...layerOps.getFiwareParams(layer),
            fiwareTokenURL: layerOps.getURL(layer)
          },
          locationIndex: layerOps.getLocationIndex(layer),
          spatialTableFields: layerOps.getTableSpatialObject(layer)
        });
        break;
      case layerOps.isGeoJSON(layer):
        geojsonFile = await loadGeoJSONFromURL(url);
        break;
      case layerOps.isJoin(layer):
        geojsonFile = await joinGeoJSONThematicLayer({
          geojsonId: layerOps.getGeoJSONId(layer),
          geojsonPropToJoin: layerOps.getGeoJSONPropToJoin(layer),
          layerSubType: layerOps.getSubType(layer),
          sorensenDiceMinimun: layerOps.getSorensenDiceMinimun(layer),
          thematicId: layerOps.getThematicId(layer),
          thematicKeyToJoin: layerOps.getThematicKeyToJoin(layer)
        });
        break;
      case layerOps.isOGR(layer):
        geojsonFile = await methodPromise('layers.loadOGRGeoJSON', {
          attribute: layerOps.getOGRAttributes(layer).split(','),
          layerName: layerOps.getOGRLayerName(layer),
          url
        });
        break;
      case layerOps.isPostgreSQL(layer):
        geojsonFile = await methodPromise('layers.loadPostgreSQL', {
          attributes: map(layerOps.getGeoJSONProps(layer), 'name'),
          loadParameters: layerOps.getConnectionParams(layer)
        });
        break;
      default:
        break;
    }
    const hexToRgb = (hex, opacity) => {
      const shorthandRegex = /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i;
      const expandShorthandHex = value => {
        const matches = shorthandRegex.exec(value);
        if (!matches) {
          throw new Error('Invalid hex value');
        }
        const r = parseInt(matches[1], 16);
        const g = parseInt(matches[2], 16);
        const b = parseInt(matches[3], 16);
        return `${r * 256 + g} ${g * 256 + b}`;
      };
      const newHex = hex.replace(shorthandRegex, expandShorthandHex);

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(newHex);
      if (!result) {
        return null;
      }
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);

      return [r, g, b, opacity * 255];
    };
    const getFillColor = d => {
      const { propName, styleData } = style.geojsonStyle;
      const result = isEmpty(propName)
        ? styleData
        : styleData.legend.find(e => e.value === d.properties[propName]);
      const COLOR = hexToRgb(result.color, styleData.alpha);
      return COLOR;
    };
    return new GeoJsonLayer({
      id: layerOps.getId(layer),
      data: geojsonFile,
      pickable: true,
      stroked: false,
      filled: true,
      extruded: true,
      visible: FindLayerVisible(layerOps.getId(layer)),
      lineWidthScale: 20,
      lineWidthMinPixels: 20,
      getFillColor,
      wireframe: true,
      getLineColor: [0, 0, 0],
      getLineWidth: 10,
      getElevation: 3,
      updateTriggers: {
        visible: [activeLayers]
      }
    });
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

export default buildDeckGeoJSONLayer;
